import { useContext, useEffect, useMemo, useState } from 'react';
import { vehicleDataService } from '../../../services/vehicle-data-service/vehicle-data-service';
import {
    VehicleDataVinApiResponse,
    VehicleDataYmmApiResponse,
    VehicleDataYMMFeatures,
} from '../../../services/vehicle-data-service/vehicle-data-service.types';
import ServerContext from '../../../contexts/serverContext';
import { useParams } from 'react-router-dom';
import {
    AuthenticationState,
    useAuthentication,
} from '../../use-authentication';
import {
    EligibleModelData,
    useDyfEligibleModel,
} from '../use-dyf-eligible-model/use-dyf-eligible-model';

import {
    filterVehicleFeaturesUnAuth,
    filterVinResponse,
    filterYmmResponse,
    transformFeaturesDto,
} from './features-utils';
import { SinglePublicFeature, UiFeature } from './vehicle-features.types';
import {
    extractCategories,
    getHiddenCategories,
    getPublicCategories,
} from './category-utils';
import slugify from 'slugify';

export async function getYmmResponse(
    current3LetterCountryCode: string,
    currentLanguageRegionCode: string,
    year: string,
    model: string
): Promise<VehicleDataYmmApiResponse | null> {
    return vehicleDataService
        .getVehicleDataYmm({
            countryCode: current3LetterCountryCode.toUpperCase(),
            language: currentLanguageRegionCode.replace('-', '_'),
            marketingModelYear: year,
            nameplate: model,
        })
        .then(res => {
            return res;
        });
}

export async function getVinResponse(
    currentLanguageRegionCode: string,
    vin: string
): Promise<VehicleDataVinApiResponse | null> {
    return vehicleDataService
        .getVinVehicleData(currentLanguageRegionCode, vin)
        .then(res => {
            if (res?.features && res.features.length > 0) {
                return res;
            } else return null;
        });
}

export interface UseYourFeaturesHook {
    loading: boolean;
    authorizedFeatures: UiFeature[] | null;
    allFeatures: UiFeature[] | null;
    dyfEligibleModel: EligibleModelData | null;
    visibleCategories: SinglePublicFeature[];
    hiddenCategories: SinglePublicFeature[];
}

export const useYourVehicleFeaturesAndCategories = (): UseYourFeaturesHook => {
    const [auth] = useAuthentication();

    const isAuthenticated: boolean = useMemo(() => {
        return auth === AuthenticationState.Authenticated;
    }, [auth]);

    const {
        vin: queryVin = '',
        year: queryYear = '',
        model: queryModel = '',
    } = useParams<{
        vin?: string;
        year?: string;
        model?: string;
    }>();

    const { current3LetterCountryCode, currentLanguageRegionCode } = useContext(
        ServerContext
    );

    const eligibleQuery = useMemo(
        () =>
            queryVin !== ''
                ? { vin: queryVin }
                : { modelAlias: queryModel, year: queryYear },
        [queryVin, queryModel, queryYear]
    );

    const {
        data: dyfEligibleModel,
        loading: dyfEligibleLoading,
    } = useDyfEligibleModel(eligibleQuery);

    const [state, setState] = useState<{
        features: VehicleDataYMMFeatures[] | null;
        loading: boolean;
        unauthFeatures: VehicleDataYMMFeatures[] | null;
    }>({
        loading: true,
        features: null,
        unauthFeatures: null,
    });

    const visibleCategories = useMemo(() => {
        if (!isAuthenticated && dyfEligibleModel) {
            return getPublicCategories(
                dyfEligibleModel?.vehicleType,
                currentLanguageRegionCode
            );
        } else if (isAuthenticated && dyfEligibleModel) {
            return extractCategories(state.features || []).map(f => ({
                displayName: f.name,
                queryName: slugify(f.name),
            }));
        } else return [];
    }, [dyfEligibleModel, isAuthenticated, state.features]);

    const hiddenCategories = useMemo(() => {
        if (
            !isAuthenticated &&
            state.features &&
            state.features?.length > 0 &&
            dyfEligibleModel
        ) {
            return getHiddenCategories(
                dyfEligibleModel?.vehicleType,
                currentLanguageRegionCode,
                extractCategories(state.features).flatMap(c => c.name)
            );
        } else return [];
    }, [dyfEligibleModel, state.features, isAuthenticated]);

    const fetchFeatures = async () => {
        let vehicleFeatures: VehicleDataYMMFeatures[] = [];
        if (queryVin !== '') {
            const res = await getVinResponse(
                currentLanguageRegionCode,
                queryVin
            );

            const filteredFeatures = filterVinResponse(res?.features || []);

            vehicleFeatures = transformFeaturesDto(filteredFeatures);
        } else if (queryYear !== '' && queryModel !== '') {
            const res = await getYmmResponse(
                current3LetterCountryCode,
                currentLanguageRegionCode,
                queryYear,
                queryModel
            );

            vehicleFeatures = res?.features || [];
        }
        return filterYmmResponse(vehicleFeatures);
    };

    useEffect(() => {
        fetchFeatures().then(features => {
            setState(prev => ({ ...prev, features, loading: false }));
        });
    }, [
        queryYear,
        queryModel,
        queryVin,
        current3LetterCountryCode,
        currentLanguageRegionCode,
    ]);

    useEffect(() => {
        if (
            !dyfEligibleLoading &&
            state.features &&
            !state.unauthFeatures &&
            dyfEligibleModel
        ) {
            const filteredFeatures = filterVehicleFeaturesUnAuth({
                vehicleType: dyfEligibleModel.vehicleType,
                allFeatures: state.features,
                currentLanguageRegionCode,
            });
            setState(prev => ({
                ...prev,
                unauthFeatures: filterYmmResponse(filteredFeatures || []),
            }));
        }
    }, [
        state.features,
        dyfEligibleLoading,
        state.unauthFeatures,
        dyfEligibleModel,
        currentLanguageRegionCode,
    ]);

    return {
        loading: state.loading,
        authorizedFeatures: isAuthenticated
            ? state.features
            : state.unauthFeatures,
        allFeatures: state.features,
        dyfEligibleModel: dyfEligibleModel,
        visibleCategories,
        hiddenCategories,
    };
};
